import React, { useState } from 'react';
import { I, initials } from './ui.js';
import { useStore, activeEntity, hiddenScenarios } from './store.jsx';
import { Matrix } from './components/Matrix.jsx';
import {
  CellDrawer, ScenarioDrawer, NoRegretDrawer, MostLikelyDrawer,
  BlindspotsDrawer, CapabilityModal, EntityEditor,
} from './components/Drawers.jsx';
import { StrategyEditor } from './components/StrategyEditor.jsx';
import { SignalsDrawer } from './components/SignalsDrawer.jsx';
import { WorkspaceDrawer } from './components/WorkspaceDrawer.jsx';
import { cell, toWeights, type ScenarioId } from './local.js';

function Login() {
  const { login } = useStore();
  const [email, setEmail] = useState('owner@flourish.local');
  const [password, setPassword] = useState('flourish');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [sso, setSso] = useState<{ enabled: boolean; loginUrl: string | null } | null>(null);
  React.useEffect(() => { import('./api.js').then(m => m.api.sso().then(setSso).catch(() => setSso(null))); }, []);
  const go = async () => {
    setBusy(true); setErr('');
    try { await login(email, password); }
    catch { setErr('Invalid credentials'); }
    setBusy(false);
  };
  return (
    <div className="login-wrap">
      <div className="login">
        <h1>Flourish</h1>
        <div className="sub">Strategy across the futures · Iceberg Future Labs</div>
        {err && <div className="err">{err}</div>}
        <div className="field"><label>Email</label>
          <input className="input" value={email} onChange={e => setEmail(e.target.value)} /></div>
        <div className="field"><label>Password</label>
          <input className="input" type="password" value={password} onChange={e => setPassword(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') void go(); }} /></div>
        <button className="btn primary" style={{ width: '100%', justifyContent: 'center' }} disabled={busy} onClick={() => void go()}>
          Sign in
        </button>
        {sso?.enabled && sso.loginUrl && (
          <a className="btn" style={{ width: '100%', justifyContent: 'center', marginTop: 8, textDecoration: 'none' }} href={sso.loginUrl}>
            <I k="globe" /> Sign in with SSO
          </a>
        )}
        <div className="sub" style={{ marginTop: 12, marginBottom: 0 }}>Dev build — credentials are prefilled.</div>
      </div>
    </div>
  );
}

function Topbar() {
  const { s, set, toast } = useStore();
  const e = activeEntity(s);
  const [menu, setMenu] = useState(false);
  if (!e) return null;
  const others = s.entities.filter(x => x.id !== e.id);
  return (
    <header className="topbar">
      <div className="brand">
        <div className="brand-mark" aria-hidden>
          <span dangerouslySetInnerHTML={{ __html: '<svg viewBox="0 0 24 24" fill="none"><path d="M12 3c2.5 3 2.5 6 0 9M12 21c-2.5-3-2.5-6 0-9M12 12c-3-2.5-6-2.5-9 0M12 12c3-2.5 6-2.5 9 0" stroke="#fff" stroke-width="1.7" stroke-linecap="round"/><circle cx="12" cy="12" r="1.9" fill="#fff"/></svg>' }} />
        </div>
        <div><span className="brand-name">Flourish<span className="tm">β</span></span></div>
        <span className="brand-tag">Iceberg Future Labs</span>
      </div>
      <div className="top-spacer" />
      <div className="top-group">
        <div className="ent-switch">
          <button className="trigger" onClick={() => setMenu(m => !m)}>
            <span className="ent-avatar" style={{ background: e.accent }}>{initials(e.name)}</span>
            <span>{e.name}</span>
            <I k="chevD" />
          </button>
          <div className={`menu ${menu ? 'open' : ''}`}>
            <div className="menu-label">Active entity</div>
            <button className="menu-item active" onClick={() => { setMenu(false); set({ drawer: { kind: 'entity-editor', entityId: e.id } }); }}>
              <span className="ent-avatar" style={{ background: e.accent }}>{initials(e.name)}</span>
              <span>{e.name}</span><span className="meta">Edit</span>
            </button>
            <div className="menu-label">Switch</div>
            {others.map(o => (
              <button key={o.id} className="menu-item" onClick={() => { setMenu(false); set({ activeEntityId: o.id }); toast(`Now viewing ${o.name}`); }}>
                <span className="ent-avatar" style={{ background: o.accent }}>{initials(o.name)}</span>
                <span>{o.name}</span><span className="meta">{o.type}</span>
              </button>
            ))}
            <div className="menu-sep" />
            <button className="menu-item" onClick={() => { setMenu(false); set({ drawer: { kind: 'entity-editor', entityId: null } }); }}>
              <span className="ent-avatar" style={{ background: 'var(--surface-3)', color: 'var(--ink-3)' }}><I k="plus" /></span>
              <span>Add entity to compare</span>
            </button>
            <button className="menu-item" onClick={() => { setMenu(false); set({ drawer: { kind: 'workspace' } }); }}>
              <span className="ent-avatar" style={{ background: 'var(--surface-3)', color: 'var(--ink-3)' }}><I k="owner" /></span>
              <span>Account &amp; workspace</span><span className="meta">{s.user?.role}</span>
            </button>
          </div>
        </div>
      </div>
      <div className="top-group">
        <button className="pill" title="World events proposing probability changes — you decide"
          onClick={() => set({ drawer: { kind: 'signals' } })}>
          <span className={`dot ${s.signals.some(x => x.status === 'proposed') ? 'pulse' : ''}`} />
          <span>{s.signals.filter(x => x.status === 'proposed').length} signals</span>
        </button>
        <button className="btn icon theme-toggle" title="Toggle theme"
          onClick={() => set({ theme: s.theme === 'light' ? 'dark' : 'light' })}>
          <I k={s.theme === 'light' ? 'eye' : 'eyeOff'} />
        </button>
      </div>
    </header>
  );
}

function Summary() {
  const { s, set } = useStore();
  const e = activeEntity(s);
  if (!e) return null;
  const focus = s.scenarios.filter(x => x.focus && !x.hidden && x.prob > 0).length;
  const hid = hiddenScenarios(s);
  const nr = (s.strategiesByEntity[e.id] ?? []).find(x => x.isNoRegret) ?? (s.strategiesByEntity[e.id] ?? [])[0];
  const anyDeadly = nr && hid.some(scn => {
    if (scn.prob <= 0) return false;
    const r = cell(e, nr, scn.id as ScenarioId);
    return r.horizons[3].death;
  });
  return (
    <section className="summary">
      <div className="sum-title">
        <span className="eyebrow">Scenario × Strategy matrix</span>
        <h2>
          <span className="ent-avatar" style={{ background: e.accent, width: 20, height: 20, borderRadius: 6 }}>{initials(e.name)}</span>
          {e.name}
        </h2>
        <span className="sub">{e.type} · 2026 → 2076 · <b>{focus}</b> in focus{hid.length > 0 && <> · <b>{hid.length}</b> hidden</>}</span>
      </div>
      {hid.length > 0 && (
        <button className="pill bs-trigger" title="Hidden scenarios you're not optimising for" onClick={() => set({ drawer: { kind: 'blindspots' } })}>
          <I k="eyeOff" /><span>Blind spots</span><span className={`bs-count ${anyDeadly ? 'alert' : ''}`}>{hid.length}</span>
        </button>
      )}
    </section>
  );
}

export function App() {
  const { s, set } = useStore();

  if (s.loading) {
    return <div className="login-wrap"><div className="muted">Loading…</div></div>;
  }
  if (!s.user) return <Login />;
  const _weights = toWeights(s.scenarios); // warm the local engine caches consistently
  void _weights;

  const d = s.drawer;
  return (
    <div className="app">
      <Topbar />
      <Summary />
      <Matrix />
      {d?.kind === 'cell' && <CellDrawer d={d} />}
      {d?.kind === 'iv-cell' && <CellDrawer d={d} />}
      {d?.kind === 'scenario' && <ScenarioDrawer d={d} />}
      {d?.kind === 'strategy-editor' && <StrategyEditor d={d} />}
      {d?.kind === 'no-regret' && <NoRegretDrawer d={d} />}
      {d?.kind === 'most-likely' && <MostLikelyDrawer d={d} />}
      {d?.kind === 'blindspots' && <BlindspotsDrawer />}
      {d?.kind === 'entity-editor' && <EntityEditor d={d} />}
      {d?.kind === 'signals' && <SignalsDrawer />}
      {d?.kind === 'workspace' && <WorkspaceDrawer />}
      <CapabilityModal />
      <div className={`toast ${s.toast ? 'show' : ''}`}>{s.toast}</div>
      {!d && <div className="scrim" style={{ display: 'none' }} onClick={() => set({ drawer: null })} />}
    </div>
  );
}
